import { memo, useCallback, useRef, useState } from 'react';
import { Plus, X } from 'lucide-react';

/**
 * Controlled list of conversation-starter strings.
 *
 * Props:
 *   value        – string[]  — current starters
 *   onChange     – (starters: string[]) => void
 *   label        – string    — section heading
 *   placeholder  – string    — input placeholder
 *   maxItems     – number    — optional cap (default: none)
 */
const ConversationStarters = memo(({ value = [], onChange, label, placeholder, maxItems }) => {
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);

  const canAdd = draft.trim().length > 0 && (maxItems == null || value.length < maxItems);

  const handleAdd = useCallback(() => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    onChange?.([...value, trimmed]);
    setDraft('');
    inputRef.current?.focus();
  }, [draft, value, onChange]);

  const handleRemove = useCallback(
    (idx) => {
      onChange?.(value.filter((_, i) => i !== idx));
    },
    [value, onChange],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAdd();
      }
    },
    [handleAdd],
  );

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <p className="text-sm font-medium text-content">{label}</p>
      )}

      <div className="flex flex-col divide-y divide-edge overflow-hidden rounded-lg border border-edge bg-surface">
        {value.map((starter, idx) => (
          <div
            key={`${starter}-${idx}`}
            className="flex min-h-[44px] items-center gap-3 px-3 py-2.5"
          >
            <span className="flex-1 text-sm text-content">{starter}</span>
            <button
              type="button"
              onClick={() => handleRemove(idx)}
              className="shrink-0 rounded p-0.5 text-content-muted transition-colors hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2173FF]"
              aria-label="Remove"
            >
              <X size={14} />
            </button>
          </div>
        ))}

        {/* Add row */}
        <div className="flex min-h-[44px] items-center gap-3 px-3 py-2.5">
          <input
            ref={inputRef}
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={maxItems != null && value.length >= maxItems}
            className="flex-1 bg-transparent text-sm text-content placeholder:text-content-faint focus:outline-none disabled:opacity-50"
          />
          <button
            type="button"
            onClick={handleAdd}
            disabled={!canAdd}
            className="shrink-0 rounded p-0.5 text-content-muted transition-colors hover:text-[#2173FF] disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2173FF]"
            aria-label="Add"
          >
            <Plus size={16} />
          </button>
        </div>
      </div>
    </div>
  );
});

ConversationStarters.displayName = 'ConversationStarters';

export default ConversationStarters;
